import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Paper,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { mainApi, accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import { getBankAccountsMap } from '../../../services/migration/financialDataService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

interface DailyClosingModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  branchId: string;
  branchName: string;
  onSuccess?: () => void;
}

// Banka hesabı interface'i
interface BankAccount {
  id: string;
  bankName: string;
  branchName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  subAccounts?: { [key: string]: SubAccount };
}

// Alt hesap interface'i
interface SubAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  parentId: string;
}

// Gün sonu özet bilgileri
interface DailyClosingSummary {
  totalOrders: number;
  totalRevenue: number;
  completedOrders: number;
  cancelledOrders: number;
  completedRevenue: number;
  lastClosingDate?: string;
  periodStart: string;
  periodEnd: string;
}

const DailyClosingModal: React.FC<DailyClosingModalProps> = ({
  open,
  onClose,
  companyId,
  branchId,
  branchName,
  onSuccess
}) => {
  const { t } = useTranslation('salesSystem');
  // State tanımlamaları
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Banka hesapları
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedBankId, setSelectedBankId] = useState<string>('');
  const [selectedSubAccountId, setSelectedSubAccountId] = useState<string>('');
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | SubAccount | null>(null);

  // Gün sonu bilgileri
  const [summary, setSummary] = useState<DailyClosingSummary | null>(null);
  const [notes, setNotes] = useState<string>('');

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (open && companyId && branchId) {
      loadData();
    }
  }, [open, companyId, branchId]);

  // Modal kapanınca state'leri sıfırla
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setError(null);
    setSuccessMessage(null);
    setBankAccounts([]);
    setSelectedBankId('');
    setSelectedSubAccountId('');
    setSelectedAccount(null);
    setSummary(null);
    setNotes('');
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Paralel olarak banka hesaplarını ve gün sonu özetini yükle
      await Promise.all([
        loadBankAccounts(),
        loadDailyClosingSummary()
      ]);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      setError(t('qrMenuDailyClosingModal.errors.dataLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Banka hesaplarını SQL'den yükle (financialDataService — GET /api/BankAccount)
  const loadBankAccounts = async () => {
    try {
      const bankMap = await getBankAccountsMap(companyId);
      const accounts: BankAccount[] = Object.values(bankMap).map((b) => {
        let subAccounts: { [key: string]: SubAccount } | undefined;
        if (b.hesaplar && Object.keys(b.hesaplar).length > 0) {
          subAccounts = {};
          for (const [subId, sub] of Object.entries(b.hesaplar)) {
            subAccounts[subId] = {
              id: sub.id,
              accountName: sub.accountName,
              accountNumber: sub.accountNumber,
              iban: sub.iban,
              currency: sub.currency,
              balance: sub.balance,
              parentId: b.id,
            };
          }
        }
        return {
          id: b.id,
          bankName: b.bankName,
          branchName: b.branchName,
          accountNumber: b.accountNumber,
          iban: b.iban,
          currency: b.currency,
          balance: b.balance,
          subAccounts,
        };
      });

      // Banka adına göre sırala
      accounts.sort((a, b) => a.bankName.localeCompare(b.bankName));
      setBankAccounts(accounts);

      // İlk bankayı otomatik seç
      if (accounts.length > 0) {
        setSelectedBankId(accounts[0].id);
        setSelectedAccount(accounts[0]);
      }
    } catch (error) {
      console.error('Banka hesapları yüklenirken hata:', error);
      throw error;
    }
  };

  // Gün sonu özetini API'den yükle
  const loadDailyClosingSummary = async () => {
    try {
      let lastClosingDate: string | undefined;
      try {
        const closingsResponse = await mainApi.get(
          `/api/DailyClosing?branchId=${branchId}&pageSize=1`
        );
        const closingsResult = closingsResponse.data;
        if (closingsResult.success && closingsResult.data && closingsResult.data.length > 0) {
          lastClosingDate = closingsResult.data[0].closingDateTime;
        }
      } catch (closingErr) {
        console.warn('Gün sonu kayıtları yüklenemedi:', closingErr);
      }

      // Siparişleri şube ve tarih bazında SUNUCUDA filtrele. Eskiden bu çağrı
      // parametresizdi: şirketin tüm geçmişi çekilip (en büyük şirkette 209 bin
      // sipariş / ~253 MB) tarayıcıda süzülüyordu. Filtre gönderilmediğinde backend
      // artık varsayılan olarak son 2 günü döndürdüğü için tarihi AÇIKÇA göndermek
      // zorunlu; aksi halde son gün sonu 2 günden eskiyse tutarlar eksik hesaplanır.
      const orderParams = new URLSearchParams();
      if (branchId) orderParams.set('branchId', branchId);

      // Backend `SiparisOlusturmaTarihi >= startDate.Date` uyguladığı için gün başlangıcı
      // alınır; kesin gün sonu saati filtresi aşağıda istemci tarafında yapılıyor.
      // Hiç gün sonu yapılmamışsa dönem başı "en baştan" demektir, o yüzden varsayılan
      // pencereyi devre dışı bırakan çok eski bir tarih gönderilir.
      const periodStartParam = lastClosingDate
        ? new Date(lastClosingDate).toISOString().slice(0, 10)
        : '2000-01-01';
      orderParams.set('startDate', periodStartParam);

      const ordersResponse = await mainApi.get(`/api/MasaSiparisi/orders?${orderParams.toString()}`);
      const ordersResult = ordersResponse.data;
      if (ordersResult.success && ordersResult.data) {
          console.log('🔍 [DailyClosing] Tüm siparişler:', {
            totalOrdersFromAPI: ordersResult.data.length,
            allOrders: ordersResult.data.map((order: any) => ({
              siparisId: order.siparisId,
              branchId: order.branchId,
              branchName: order.branchName,
              toplamTutar: order.toplamTutar,
              siparisDurumu: order.siparisDurumu,
              siparisOlusturmaTarihi: order.siparisOlusturmaTarihi
            }))
          });

          // Bu şubeye ait siparişleri filtrele
          const branchOrders = ordersResult.data.filter((order: any) => 
            order.branchId === branchId
          );

          console.log('🏢 [DailyClosing] Şube filtreleme:', {
            targetBranchId: branchId,
            branchOrdersCount: branchOrders.length,
            branchOrders: branchOrders.map((order: any) => ({
              siparisId: order.siparisId,
              branchId: order.branchId,
              branchName: order.branchName,
              toplamTutar: order.toplamTutar,
              siparisDurumu: order.siparisDurumu,
              siparisOlusturmaTarihi: order.siparisOlusturmaTarihi
            }))
          });

          // Gün sonu tarihinden sonraki siparişleri filtrele
          let filteredOrders = branchOrders;
          let periodStart = t('qrMenuDailyClosingModal.summary.periodStartDefault');

          if (lastClosingDate) {
            // Gün sonu işleminin yapıldığı gerçek tarih-saati kullan
            const lastClosingDateTime = new Date(lastClosingDate);
            
            console.log('📅 [DailyClosing] Tarih filtreleme:', {
              lastClosingDate: lastClosingDate,
              lastClosingDateTime: lastClosingDateTime.toISOString(),
              branchOrdersBeforeFilter: branchOrders.length
            });

            filteredOrders = branchOrders.filter((order: any) => {
              const orderDate = new Date(order.siparisOlusturmaTarihi);
              const isAfterClosing = orderDate > lastClosingDateTime;
              console.log('📅 [DailyClosing] Sipariş tarih kontrolü:', {
                siparisId: order.siparisId,
                orderDate: orderDate.toISOString(),
                lastClosingDateTime: lastClosingDateTime.toISOString(),
                isAfterClosing: isAfterClosing,
                orderDateMs: orderDate.getTime(),
                closingDateMs: lastClosingDateTime.getTime(),
                diffMs: orderDate.getTime() - lastClosingDateTime.getTime()
              });
              return isAfterClosing;
            });
            periodStart = new Date(lastClosingDate).toLocaleDateString('tr-TR');
          }

          console.log('✅ [DailyClosing] Final filtreli siparişler:', {
            filteredOrdersCount: filteredOrders.length,
            periodStart: periodStart,
            filteredOrders: filteredOrders.map((order: any) => ({
              siparisId: order.siparisId,
              branchId: order.branchId,
              branchName: order.branchName,
              toplamTutar: order.toplamTutar,
              siparisDurumu: order.siparisDurumu,
              siparisOlusturmaTarihi: order.siparisOlusturmaTarihi
            }))
          });

          // İstatistikleri hesapla - TÜM SİPARİŞLER GÜN SONUNA DAHİL
          const totalOrders = filteredOrders.length;
          const totalRevenue = filteredOrders.reduce((sum: number, order: any) => sum + order.toplamTutar, 0);
          const completedOrders = filteredOrders.filter((order: any) => 
            order.siparisDurumu.toLowerCase() === 'teslim edildi'
          );
          const cancelledOrders = filteredOrders.filter((order: any) => 
            order.siparisDurumu.toLowerCase().includes('iptal')
          );
          
          // GÜN SONU AKTARILACAK TUTAR = TÜM SİPARİŞLERİN TOPLAMI (durum fark etmez)
          const dailyClosingRevenue = totalRevenue; // Tüm siparişler dahil

          console.log('💰 [DailyClosing] Ciro hesaplamaları:', {
            totalOrders: totalOrders,
            totalRevenue: totalRevenue,
            completedOrdersCount: completedOrders.length,
            completedOrders: completedOrders.map((order: any) => ({
              siparisId: order.siparisId,
              toplamTutar: order.toplamTutar,
              siparisDurumu: order.siparisDurumu
            })),
            cancelledOrdersCount: cancelledOrders.length,
            dailyClosingRevenue: dailyClosingRevenue,
            note: 'TÜM SİPARİŞLER GÜN SONUNA DAHİL EDİLDİ (durum fark etmez)'
          });

          setSummary({
            totalOrders,
            totalRevenue,
            completedOrders: completedOrders.length,
            cancelledOrders: cancelledOrders.length,
            completedRevenue: dailyClosingRevenue, // Tüm siparişlerin toplamı
            lastClosingDate,
            periodStart,
            periodEnd: new Date().toLocaleDateString('tr-TR')
          });
      }
    } catch (error) {
      console.error('Gün sonu özeti yüklenirken hata:', error);
      throw error;
    }
  };

  // Banka seçimi değiştiğinde
  const handleBankChange = (bankId: string) => {
    setSelectedBankId(bankId);
    setSelectedSubAccountId('');
    
    const bank = bankAccounts.find(b => b.id === bankId);
    if (bank) {
      setSelectedAccount(bank);
    }
  };

  // Alt hesap seçimi değiştiğinde
  const handleSubAccountChange = (subAccountId: string) => {
    setSelectedSubAccountId(subAccountId);
    
    const bank = bankAccounts.find(b => b.id === selectedBankId);
    if (bank && bank.subAccounts && subAccountId) {
      const subAccount = bank.subAccounts[subAccountId];
      if (subAccount) {
        setSelectedAccount({
          ...subAccount,
          id: subAccountId,
          parentId: bank.id
        } as SubAccount);
      }
    } else if (bank) {
      setSelectedAccount(bank);
    }
  };

  // Para birimi sembolü
  const getCurrencySymbol = (currency: string): string => {
    switch(currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return currency;
    }
  };

  // Gün sonu işlemini gerçekleştir
  const handleDailyClosing = async () => {
    if (!selectedAccount || !summary) {
      setError(t('qrMenuDailyClosingModal.errors.selectAccount'));
      return;
    }

    if (summary.completedRevenue <= 0) {
      setError(t('qrMenuDailyClosingModal.errors.transferAmountMustBePositive'));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        throw new Error(t('qrMenuDailyClosingModal.errors.userSessionMissing'));
      }

      // 1. Gün sonu kaydını oluştur
      await mainApi.post(`/api/DailyClosing/create`, {
        qrMenuId: branchId,
        closingDate: new Date().toISOString().split('T')[0],
        notes: notes.trim() || null
      });

      // 2. Banka hesabına para transferi yap (SQL deposit — bakiye + işlem kaydı sunucu tarafında)
      const transferAmount = summary.completedRevenue; // Bu artık totalRevenue'ye eşit
      const isSubAccount = 'parentId' in selectedAccount;

      const numericBankAccountId = parseInt(selectedBankId, 10);
      if (isNaN(numericBankAccountId)) {
        throw new Error(t('qrMenuDailyClosingModal.errors.processFailed'));
      }
      const numericSubAccountId = isSubAccount ? parseInt(selectedSubAccountId, 10) : null;

      await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_DEPOSIT(numericBankAccountId), {
        bankSubAccountId: numericSubAccountId,
        amount: transferAmount,
        description: t('qrMenuDailyClosingModal.transferDescription', { branchName, periodStart: summary.periodStart, periodEnd: summary.periodEnd }),
        createdBy: ownAuthSession.getCurrentEmail() || uid || 'System',
      });

      // Başarı mesajı
      setSuccessMessage(
        t('qrMenuDailyClosingModal.messages.success', {
          amount: transferAmount.toLocaleString('tr-TR'),
          currency: getCurrencySymbol(selectedAccount.currency),
          orderCount: summary.totalOrders
        })
      );

      // Başarı callback'i çağır
      if (onSuccess) {
        onSuccess();
      }

      // 3 saniye sonra modalı kapat
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (error: any) {
      console.error('Gün sonu işlemi hatası:', error);
      setError(error.message || t('qrMenuDailyClosingModal.errors.processFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={!saving ? onClose : undefined}
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6">{t('qrMenuDailyClosingModal.title')}</Typography>
            <GuideButton guideKey="sales.qrDailyClosing" size="small" />
            <Chip label={branchName} color="primary" variant="outlined" size="small" />
          </Box>
          <IconButton onClick={onClose} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Gün Sonu Özeti */}
            {summary && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon />
                  {t('qrMenuDailyClosingModal.summary.title')}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.summary.period')}</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {summary.periodStart} - {summary.periodEnd}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.summary.totalOrders')}</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {t('qrMenuDailyClosingModal.summary.orderCountValue', { count: summary.totalOrders })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.summary.completedOrders')}</Typography>
                    <Typography variant="body1" fontWeight="medium" color="success.main">
                      {t('qrMenuDailyClosingModal.summary.orderCountValue', { count: summary.completedOrders })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.summary.cancelledOrders')}</Typography>
                    <Typography variant="body1" fontWeight="medium" color="error.main">
                      {t('qrMenuDailyClosingModal.summary.orderCountValue', { count: summary.cancelledOrders })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.summary.totalRevenue')}</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {summary.totalRevenue.toLocaleString('tr-TR')} ₺
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.summary.transferAmount')}</Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      {summary.completedRevenue.toLocaleString('tr-TR')} ₺
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Banka Hesabı Seçimi */}
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalanceIcon />
              {t('qrMenuDailyClosingModal.accountSelection.title')}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('qrMenuDailyClosingModal.accountSelection.selectBank')}</InputLabel>
                  <Select
                    value={selectedBankId}
                    label={t('qrMenuDailyClosingModal.accountSelection.selectBank')}
                    onChange={(e) => handleBankChange(e.target.value)}
                  >
                    {bankAccounts.map((bank) => (
                      <MenuItem key={bank.id} value={bank.id}>
                        {bank.bankName} - {bank.branchName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {selectedBankId && bankAccounts.find(b => b.id === selectedBankId)?.subAccounts && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('qrMenuDailyClosingModal.accountSelection.selectSubAccount')}</InputLabel>
                    <Select
                      value={selectedSubAccountId}
                      label={t('qrMenuDailyClosingModal.accountSelection.selectSubAccount')}
                      onChange={(e) => handleSubAccountChange(e.target.value)}
                    >
                      <MenuItem value="">{t('qrMenuDailyClosingModal.accountSelection.mainAccount')}</MenuItem>
                      {Object.entries(bankAccounts.find(b => b.id === selectedBankId)?.subAccounts || {}).map(([id, subAccount]) => (
                        <MenuItem key={id} value={id}>
                          {subAccount.accountName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {/* Seçili Hesap Bilgileri */}
            {selectedAccount && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>{t('qrMenuDailyClosingModal.selectedAccount.title')}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.selectedAccount.account')}</Typography>
                    <Typography variant="body2">
                      {'parentId' in selectedAccount 
                        ? selectedAccount.accountName 
                        : t('qrMenuDailyClosingModal.selectedAccount.mainAccountLabel', { bankName: selectedAccount.bankName })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.selectedAccount.currentBalance')}</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedAccount.balance.toLocaleString('tr-TR')} {getCurrencySymbol(selectedAccount.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.selectedAccount.accountNumber')}</Typography>
                    <Typography variant="body2">{selectedAccount.accountNumber}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('qrMenuDailyClosingModal.selectedAccount.balanceAfter')}</Typography>
                    <Typography variant="body2" fontWeight="medium" color="success.main">
                      {summary ? (selectedAccount.balance + summary.completedRevenue).toLocaleString('tr-TR') : '0'} {getCurrencySymbol(selectedAccount.currency)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Notlar */}
            <TextField
              fullWidth
              label={t('qrMenuDailyClosingModal.notes.label')}
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('qrMenuDailyClosingModal.notes.placeholder')}
              sx={{ mb: 2 }}
            />

            {/* Mesajlar */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('qrMenuDailyClosingModal.actions.cancel')}
        </Button>
        <Button
          onClick={handleDailyClosing}
          variant="contained"
          disabled={
            loading || 
            saving || 
            !selectedAccount || 
            !summary || 
            summary.completedRevenue <= 0
          }
          startIcon={saving ? <CircularProgress size={20} /> : <TrendingUpIcon />}
        >
          {saving ? t('qrMenuDailyClosingModal.actions.processing') : t('qrMenuDailyClosingModal.actions.takeEndOfDay')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DailyClosingModal;
